import Phaser from 'phaser';
import {
  TX,
  TILESET_KEY,
  TILE_INDEX,
  TILESET_COLUMNS,
  cropTextureKey,
  itemIconKey,
} from './keys';
import { CONFIG } from '../data/config';
import { CROPS, type CropDef } from '../data/crops';
import { DECORATIONS } from '../data/decorations';

/**
 * Programmatic placeholder art, drawn pixel-by-pixel and baked to textures.
 * Tasteful stand-ins in a Sprout-Lands-ish pastel palette. Every key here is
 * shared with the eventual real art, so swapping = dropping files + a manifest
 * line (see assetManifest.ts). Tile size is CONFIG.tileSize (16).
 */

const TS = CONFIG.tileSize;

const PAL = {
  grass1: 0x7dbf5a,
  grass2: 0x8fd06a,
  grass3: 0x69ab4b,
  soil1: 0x8a6240,
  soil2: 0x6f4d31,
  soilWet1: 0x5b3f28,
  soilWet2: 0x4a3220,
  water1: 0x5aa7d8,
  water2: 0x82c4e8,
  water3: 0x4a8fc0,
  path1: 0xcda878,
  path2: 0xb9925f,
  wood1: 0x9a6b3f,
  wood2: 0x744f2c,
  leaf1: 0x4f9d44,
  leaf2: 0x3a7b34,
  skin: 0xf1c79b,
  hair: 0x5e3f25,
  shirt: 0x4a86c5,
  shirtAlt: 0xc55a5a,
  pants: 0x394a6b,
  catBody: 0xe8a85c,
  catDark: 0xcf8f44,
  claraDress: 0xe48ab5,
  claraHair: 0x8a5a3a,
  roof: 0xc0584e,
  roofDark: 0x9f463f,
  wall: 0xeaddc0,
  wallDark: 0xcdbf9a,
  door: 0x744f2c,
  sky: 0xbfe3f0,
  gold: 0xf2c14e,
  goldDark: 0xd49a30,
  red: 0xe0556a,
  white: 0xffffff,
  ink: 0x2c2230,
} as const;

// ---- low-level pixel helpers --------------------------------------------------

type G = Phaser.GameObjects.Graphics;

function px(g: G, x: number, y: number, c: number, a = 1): void {
  g.fillStyle(c, a);
  g.fillRect(x, y, 1, 1);
}
function box(g: G, x: number, y: number, w: number, h: number, c: number, a = 1): void {
  g.fillStyle(c, a);
  g.fillRect(x, y, w, h);
}

function mix(c1: number, c2: number, t: number): number {
  const r1 = (c1 >> 16) & 0xff, g1 = (c1 >> 8) & 0xff, b1 = c1 & 0xff;
  const r2 = (c2 >> 16) & 0xff, g2 = (c2 >> 8) & 0xff, b2 = c2 & 0xff;
  const r = Math.round(r1 + (r2 - r1) * t);
  const gg = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return (r << 16) | (gg << 8) | b;
}
const lighten = (c: number, t = 0.25): number => mix(c, 0xffffff, t);
const darken = (c: number, t = 0.25): number => mix(c, 0x000000, t);

/** Generate a single texture from a draw callback, skipping keys already loaded. */
function tex(scene: Phaser.Scene, key: string, w: number, h: number, draw: (g: G) => void): void {
  if (scene.textures.exists(key)) return;
  const g = scene.add.graphics();
  g.setVisible(false);
  draw(g);
  g.generateTexture(key, w, h);
  g.destroy();
}

// ---- terrain tiles (drawn into one packed tileset strip) ----------------------

function drawTile(g: G, index: number, ox: number): void {
  switch (index) {
    case TILE_INDEX.grass:
      box(g, ox, 0, TS, TS, PAL.grass1);
      px(g, ox + 3, 4, PAL.grass2); px(g, ox + 4, 4, PAL.grass2);
      px(g, ox + 10, 7, PAL.grass3); px(g, ox + 12, 11, PAL.grass2);
      px(g, ox + 6, 13, PAL.grass3); px(g, ox + 2, 10, PAL.grass2);
      break;
    case TILE_INDEX.grassAlt:
      box(g, ox, 0, TS, TS, PAL.grass1);
      // little flowers
      px(g, ox + 4, 5, PAL.white); px(g, ox + 4, 4, PAL.gold);
      px(g, ox + 11, 10, PAL.white); px(g, ox + 11, 9, PAL.red);
      px(g, ox + 8, 12, PAL.grass3);
      break;
    case TILE_INDEX.soilDry:
      box(g, ox, 0, TS, TS, PAL.soil1);
      box(g, ox, 3, TS, 1, PAL.soil2); box(g, ox, 8, TS, 1, PAL.soil2); box(g, ox, 13, TS, 1, PAL.soil2);
      px(g, ox + 5, 1, darken(PAL.soil1, 0.15)); px(g, ox + 11, 6, darken(PAL.soil1, 0.15));
      break;
    case TILE_INDEX.soilWet:
      box(g, ox, 0, TS, TS, PAL.soilWet1);
      box(g, ox, 3, TS, 1, PAL.soilWet2); box(g, ox, 8, TS, 1, PAL.soilWet2); box(g, ox, 13, TS, 1, PAL.soilWet2);
      px(g, ox + 6, 5, lighten(PAL.soilWet1, 0.12)); px(g, ox + 12, 10, lighten(PAL.soilWet1, 0.12));
      break;
    case TILE_INDEX.water:
      box(g, ox, 0, TS, TS, PAL.water1);
      box(g, ox + 2, 4, 5, 1, PAL.water2); box(g, ox + 9, 9, 4, 1, PAL.water2);
      box(g, ox + 4, 11, 3, 1, PAL.water3); box(g, ox + 10, 3, 3, 1, PAL.water3);
      break;
    case TILE_INDEX.path:
      box(g, ox, 0, TS, TS, PAL.path1);
      px(g, ox + 3, 3, PAL.path2); px(g, ox + 9, 6, PAL.path2);
      px(g, ox + 12, 12, PAL.path2); px(g, ox + 6, 10, PAL.path2); px(g, ox + 2, 13, PAL.path2);
      break;
    case TILE_INDEX.fence:
      // transparent background; grass shows through
      box(g, ox + 7, 1, 2, 14, PAL.wood1); // post
      box(g, ox + 7, 1, 2, 1, PAL.wood2);
      box(g, ox, 4, TS, 2, PAL.wood1); // top rail
      box(g, ox, 9, TS, 2, PAL.wood1); // bottom rail
      box(g, ox, 4, TS, 1, lighten(PAL.wood1, 0.15));
      break;
    case TILE_INDEX.tree:
      box(g, ox + 7, 10, 2, 5, PAL.wood2); // trunk
      g.fillStyle(PAL.leaf2, 1); g.fillCircle(ox + 8, 7, 6);
      g.fillStyle(PAL.leaf1, 1); g.fillCircle(ox + 7, 6, 4);
      px(g, ox + 5, 4, lighten(PAL.leaf1, 0.2));
      break;
    case TILE_INDEX.flowers:
      // decorative overlay; mostly transparent
      px(g, ox + 4, 9, PAL.red); px(g, ox + 4, 8, PAL.gold);
      px(g, ox + 8, 11, PAL.white); px(g, ox + 8, 10, PAL.claraDress);
      px(g, ox + 11, 7, PAL.gold); box(g, ox + 6, 12, 1, 2, PAL.leaf2);
      break;
    case TILE_INDEX.plank:
      box(g, ox, 0, TS, TS, PAL.wood1);
      box(g, ox, 5, TS, 1, PAL.wood2); box(g, ox, 11, TS, 1, PAL.wood2);
      px(g, ox + 8, 2, lighten(PAL.wood1, 0.1));
      break;
    default:
      box(g, ox, 0, TS, TS, PAL.grass1);
  }
}

function buildTileset(scene: Phaser.Scene): void {
  if (scene.textures.exists(TILESET_KEY)) return;
  const g = scene.add.graphics();
  g.setVisible(false);
  const count = Object.keys(TILE_INDEX).length;
  for (let i = 0; i < count; i++) drawTile(g, i, i * TS);
  g.generateTexture(TILESET_KEY, TILESET_COLUMNS * TS, TS);
  g.destroy();
}

// ---- characters ---------------------------------------------------------------

function buildCharacters(scene: Phaser.Scene): void {
  tex(scene, TX.player, TS, TS, (g) => {
    box(g, 4, 1, 8, 3, PAL.hair);
    box(g, 3, 3, 10, 1, PAL.hair);
    box(g, 4, 4, 8, 3, PAL.skin); // face
    px(g, 6, 5, PAL.ink); px(g, 9, 5, PAL.ink); // eyes
    box(g, 4, 7, 8, 4, PAL.shirt); // torso
    px(g, 3, 7, PAL.skin); px(g, 3, 8, PAL.skin); // left arm
    px(g, 12, 7, PAL.skin); px(g, 12, 8, PAL.skin); // right arm
    box(g, 5, 11, 6, 3, PAL.pants); // legs
    box(g, 5, 14, 2, 2, PAL.wood2); box(g, 9, 14, 2, 2, PAL.wood2); // boots
  });

  tex(scene, TX.pet, TS, TS, (g) => {
    box(g, 3, 9, 8, 4, PAL.catBody); // body
    box(g, 9, 6, 5, 5, PAL.catBody); // head
    g.fillStyle(PAL.catBody, 1);
    g.fillTriangle(9, 6, 11, 3, 12, 6); // ear
    g.fillTriangle(12, 6, 13, 3, 14, 6); // ear
    box(g, 2, 9, 2, 1, PAL.catDark); // tail base
    box(g, 1, 7, 1, 3, PAL.catDark); // tail
    box(g, 4, 13, 2, 2, PAL.catDark); box(g, 8, 13, 2, 2, PAL.catDark); // legs
    px(g, 11, 8, PAL.ink); px(g, 13, 8, PAL.ink); // eyes
    px(g, 12, 9, PAL.red); // nose
  });
  tex(scene, TX.petCat, TS, TS, (g) => {
    box(g, 3, 9, 8, 4, PAL.catBody);
    box(g, 9, 6, 5, 5, PAL.catBody);
    g.fillStyle(PAL.catBody, 1);
    g.fillTriangle(9, 6, 11, 3, 12, 6); g.fillTriangle(12, 6, 13, 3, 14, 6); // ears
    box(g, 2, 9, 2, 1, PAL.catDark); box(g, 1, 7, 1, 3, PAL.catDark); // tail
    box(g, 4, 13, 2, 2, PAL.catDark); box(g, 8, 13, 2, 2, PAL.catDark); // legs
    px(g, 11, 8, PAL.ink); px(g, 13, 8, PAL.ink); px(g, 12, 9, PAL.red);
  });
  tex(scene, TX.petDog, TS, TS, (g) => {
    box(g, 3, 9, 8, 4, 0xb07a4f); // body
    box(g, 9, 6, 5, 5, 0xb07a4f); // head
    box(g, 8, 5, 2, 5, 0x8a5e3a); box(g, 13, 5, 2, 5, 0x8a5e3a); // floppy ears
    box(g, 13, 9, 2, 2, 0x8a5e3a); // snout
    box(g, 2, 8, 2, 1, 0x8a5e3a); box(g, 1, 6, 1, 3, 0x8a5e3a); // tail (up)
    box(g, 4, 13, 2, 2, 0x8a5e3a); box(g, 8, 13, 2, 2, 0x8a5e3a); // legs
    px(g, 11, 8, PAL.ink); px(g, 13, 8, PAL.ink); px(g, 14, 9, PAL.ink); // eyes + nose
  });

  tex(scene, TX.clara, TS, TS, (g) => {
    const blonde = 0xf2d27a;
    // blonde hair framing the face + two little pigtails (not a hat)
    box(g, 5, 2, 6, 2, blonde);
    box(g, 4, 3, 8, 1, blonde);
    box(g, 4, 4, 1, 4, blonde);
    box(g, 11, 4, 1, 4, blonde);
    box(g, 3, 5, 1, 3, blonde); // left pigtail
    box(g, 12, 5, 1, 3, blonde); // right pigtail
    px(g, 6, 3, lighten(blonde, 0.3));
    // face
    box(g, 5, 4, 6, 4, PAL.skin);
    px(g, 6, 6, PAL.ink); px(g, 9, 6, PAL.ink); // eyes
    px(g, 5, 7, 0xe9a0a0); px(g, 10, 7, 0xe9a0a0); // rosy cheeks
    // dress
    box(g, 5, 8, 6, 2, PAL.claraDress);
    box(g, 4, 10, 8, 1, PAL.claraDress);
    px(g, 4, 8, PAL.skin); px(g, 11, 8, PAL.skin); // arms
    box(g, 5, 11, 2, 2, PAL.skin); box(g, 9, 11, 2, 2, PAL.skin); // legs
  });
}

// ---- structures ---------------------------------------------------------------

function buildStructures(scene: Phaser.Scene): void {
  tex(scene, TX.house, 48, 40, (g) => {
    box(g, 4, 16, 40, 22, PAL.wall); // body
    box(g, 4, 16, 40, 2, PAL.wallDark);
    box(g, 4, 36, 40, 2, PAL.wallDark);
    // roof
    g.fillStyle(PAL.roof, 1);
    g.fillTriangle(1, 17, 24, 2, 47, 17);
    box(g, 2, 14, 44, 4, PAL.roofDark);
    box(g, 34, 5, 5, 9, PAL.wallDark); // chimney
    // door
    box(g, 20, 26, 8, 12, PAL.door);
    px(g, 26, 32, PAL.gold);
    // windows
    box(g, 9, 21, 7, 7, PAL.sky); box(g, 9, 21, 7, 1, PAL.wallDark); px(g, 12, 21, PAL.wallDark);
    box(g, 32, 21, 7, 7, PAL.sky); box(g, 32, 21, 7, 1, PAL.wallDark); px(g, 35, 21, PAL.wallDark);
  });
  // ---- house upgrade tiers (taller/grander; bottom-anchored when rendered) ----
  tex(scene, TX.house2, 48, 46, (g) => {
    box(g, 3, 18, 42, 26, PAL.wall);
    box(g, 3, 18, 42, 2, PAL.wallDark); box(g, 3, 42, 42, 2, PAL.wallDark);
    g.fillStyle(PAL.roof, 1); g.fillTriangle(0, 19, 24, 3, 48, 19);
    box(g, 1, 16, 46, 4, PAL.roofDark);
    box(g, 21, 7, 6, 9, PAL.roof); box(g, 22, 9, 4, 5, PAL.sky); // dormer
    box(g, 36, 5, 5, 12, PAL.wallDark); // chimney
    box(g, 20, 32, 8, 12, PAL.door); px(g, 26, 38, PAL.gold);
    box(g, 7, 24, 8, 8, PAL.sky); box(g, 7, 24, 8, 1, PAL.wallDark);
    box(g, 33, 24, 8, 8, PAL.sky); box(g, 33, 24, 8, 1, PAL.wallDark);
  });
  tex(scene, TX.house3, 48, 54, (g) => {
    box(g, 2, 16, 44, 36, PAL.wall);
    box(g, 2, 16, 44, 2, PAL.wallDark); box(g, 2, 33, 44, 1, PAL.wallDark); box(g, 2, 50, 44, 2, PAL.wallDark);
    g.fillStyle(PAL.roof, 1); g.fillTriangle(0, 17, 24, 2, 48, 17);
    box(g, 1, 14, 46, 4, PAL.roofDark);
    box(g, 37, 3, 5, 12, PAL.wallDark);
    box(g, 20, 40, 9, 12, PAL.door); px(g, 27, 46, PAL.gold);
    box(g, 7, 20, 8, 8, PAL.sky); box(g, 20, 20, 8, 8, PAL.sky); box(g, 33, 20, 8, 8, PAL.sky);
    box(g, 7, 37, 8, 8, PAL.sky); box(g, 33, 37, 8, 8, PAL.sky);
  });
  tex(scene, TX.castle, 48, 64, (g) => {
    const st = 0x9aa0a8; const stD = 0x6e747c;
    box(g, 2, 22, 44, 40, st);
    box(g, 2, 22, 44, 2, stD); box(g, 2, 60, 44, 2, stD);
    for (let i = 3; i < 45; i += 8) box(g, i, 17, 5, 6, st); // battlements
    box(g, 33, 6, 13, 56, st); box(g, 33, 6, 13, 2, stD); // tower
    for (let i = 34; i < 46; i += 6) box(g, i, 2, 4, 5, st);
    box(g, 37, 12, 5, 6, 0x3a2a1e); // tower window
    box(g, 43, 0, 1, 7, PAL.wood2); g.fillStyle(0xd0584a, 1); g.fillTriangle(44, 0, 44, 4, 47, 2); // flag
    box(g, 18, 46, 12, 16, 0x2a1e14); for (let i = 19; i < 30; i += 3) box(g, i, 46, 1, 16, stD); // portcullis
    box(g, 7, 30, 7, 9, 0x3a2a1e); box(g, 20, 30, 7, 9, 0x3a2a1e);
    px(g, 9, 33, PAL.sky); px(g, 22, 33, PAL.sky);
  });
  tex(scene, TX.blueprint, 16, 16, (g) => {
    box(g, 1, 2, 14, 12, 0x2f5aa0);
    box(g, 1, 2, 14, 1, 0x4a7ad0);
    box(g, 5, 8, 6, 4, 0xcfe0ff); g.fillStyle(0xcfe0ff, 1); g.fillTriangle(4, 8, 12, 8, 8, 4);
    px(g, 7, 10, 0x2f5aa0);
  });
  tex(scene, TX.cabin, 48, 42, (g) => {
    box(g, 4, 18, 40, 22, 0x9a6b42); // log wall
    for (let yy = 22; yy < 40; yy += 5) box(g, 4, yy, 40, 1, 0x6e4a2e);
    g.fillStyle(PAL.roof, 1); g.fillTriangle(1, 19, 24, 5, 47, 19);
    box(g, 2, 16, 44, 4, PAL.roofDark);
    box(g, 34, 7, 5, 10, 0x6e4a2e); // chimney
    box(g, 20, 28, 8, 12, PAL.door); px(g, 26, 34, PAL.gold);
    box(g, 9, 23, 7, 6, PAL.sky); box(g, 33, 23, 7, 6, PAL.sky);
  });
  tex(scene, TX.estate, 48, 58, (g) => {
    box(g, 2, 22, 44, 34, PAL.wall);
    box(g, 2, 22, 44, 2, PAL.wallDark); box(g, 2, 38, 44, 1, PAL.wallDark);
    box(g, 2, 50, 44, 6, 0x9aa0a8); // stone base
    g.fillStyle(PAL.roof, 1); g.fillTriangle(0, 23, 24, 5, 48, 23);
    box(g, 1, 20, 46, 4, PAL.roofDark);
    box(g, 8, 8, 5, 13, PAL.wallDark); box(g, 35, 8, 5, 13, PAL.wallDark); // two chimneys
    box(g, 20, 44, 9, 12, PAL.door); px(g, 27, 50, PAL.gold);
    box(g, 16, 42, 2, 14, 0xe8e0d0); box(g, 30, 42, 2, 14, 0xe8e0d0); // columns
    box(g, 7, 27, 7, 8, PAL.sky); box(g, 20, 27, 8, 8, PAL.sky); box(g, 34, 27, 7, 8, PAL.sky);
    box(g, 7, 41, 7, 7, PAL.sky); box(g, 34, 41, 7, 7, PAL.sky);
  });

  tex(scene, TX.bed, TS, TS * 2, (g) => {
    box(g, 2, 2, 12, 28, PAL.wood2); // frame
    box(g, 3, 4, 10, 24, PAL.wall); // mattress
    box(g, 3, 4, 10, 7, PAL.white); // pillow
    box(g, 3, 13, 10, 15, PAL.shirt); // blanket
    box(g, 3, 13, 10, 1, lighten(PAL.shirt, 0.2));
  });

  tex(scene, TX.shippingBin, TS, TS, (g) => {
    box(g, 2, 5, 12, 9, PAL.wood1);
    box(g, 2, 3, 12, 3, PAL.wood2); // lid
    box(g, 3, 6, 10, 3, darken(PAL.wood2, 0.5)); // opening
    box(g, 2, 13, 12, 1, PAL.wood2);
    px(g, 7, 7, PAL.gold); px(g, 8, 7, PAL.gold); // hint of coin inside
  });

  tex(scene, TX.sign, TS, TS, (g) => {
    box(g, 7, 8, 2, 8, PAL.wood2); // post
    box(g, 3, 3, 10, 6, PAL.wood1); // board
    box(g, 3, 3, 10, 1, lighten(PAL.wood1, 0.2));
    box(g, 5, 5, 6, 1, PAL.wood2); box(g, 5, 7, 4, 1, PAL.wood2); // "text"
  });
  tex(scene, TX.mailbox, TS, TS, (g) => {
    box(g, 7, 8, 2, 7, PAL.wood2); // post
    box(g, 3, 3, 9, 6, 0x5a8fd0); // box
    box(g, 3, 3, 9, 1, lighten(0x5a8fd0, 0.25));
    box(g, 3, 6, 9, 1, darken(0x5a8fd0, 0.25)); // slot
    px(g, 10, 6, PAL.gold); // latch
  });
  tex(scene, TX.mailFlag, 6, 8, (g) => {
    box(g, 0, 0, 1, 8, PAL.wood2); // pole
    g.fillStyle(PAL.red, 1); g.fillTriangle(1, 0, 5, 2, 1, 4); // flag
  });
  tex(scene, TX.trailArch, TS, TS, (g) => {
    box(g, 2, 2, 2, 13, PAL.wood2); box(g, 12, 2, 2, 13, PAL.wood2); // posts
    box(g, 1, 1, 14, 2, PAL.wood1); // top beam
    g.fillStyle(PAL.leaf1, 1); g.fillCircle(5, 4, 2); g.fillCircle(11, 4, 2); // foliage
    g.fillStyle(0x3a5a2a, 1); px(g, 7, 11, 0x3a5a2a); px(g, 9, 12, 0x3a5a2a); // trail
  });
}

// ---- decorations (world texture + inventory icon) -----------------------------

function buildDecorations(scene: Phaser.Scene): void {
  const draw: Record<string, (g: G, c: number) => void> = {
    flowerpot: (g, c) => {
      box(g, 4, 9, 8, 6, PAL.wood1); box(g, 3, 8, 10, 2, PAL.wood2);
      px(g, 6, 6, c); px(g, 8, 5, c); px(g, 10, 6, c);
      px(g, 7, 7, lighten(c, 0.3)); box(g, 7, 7, 1, 2, PAL.leaf2);
    },
    lantern: (g, c) => {
      box(g, 7, 9, 2, 7, PAL.wood2); // post
      g.fillStyle(c, 0.5); g.fillCircle(8, 5, 5); // glow
      box(g, 5, 2, 6, 6, PAL.wood2); box(g, 6, 3, 4, 4, c); // lamp
      px(g, 7, 4, PAL.white);
    },
    bench: (g, c) => {
      box(g, 2, 7, 28, 3, c); box(g, 2, 7, 28, 1, lighten(c, 0.2));
      box(g, 2, 2, 28, 2, darken(c, 0.15)); // back
      box(g, 4, 4, 1, 3, darken(c, 0.2)); box(g, 27, 4, 1, 3, darken(c, 0.2));
      box(g, 4, 10, 2, 5, PAL.wood2); box(g, 26, 10, 2, 5, PAL.wood2); // legs
    },
    scarecrow: (g, c) => {
      box(g, 7, 6, 2, 24, PAL.wood2); // post
      box(g, 2, 10, 12, 2, PAL.wood2); // arms
      g.fillStyle(PAL.wall, 1); g.fillCircle(8, 6, 4); // head
      box(g, 4, 2, 8, 3, PAL.soil2); // hat
      px(g, 6, 6, PAL.ink); px(g, 10, 6, PAL.ink);
      box(g, 4, 12, 8, 7, c); // shirt
    },
    rug: (g, c) => {
      box(g, 2, 3, 28, 26, c);
      box(g, 4, 5, 24, 22, lighten(c, 0.22));
      box(g, 7, 8, 18, 16, c);
      box(g, 10, 11, 12, 10, lighten(c, 0.38));
      for (let i = 2; i < 30; i += 3) {
        box(g, i, 1, 2, 2, darken(c, 0.1));
        box(g, i, 29, 2, 2, darken(c, 0.1));
      }
    },
    pinwheel: (g, c) => {
      box(g, 7, 8, 2, 7, PAL.wood2);
      g.fillStyle(c, 1); g.fillTriangle(8, 7, 8, 2, 13, 5);
      g.fillStyle(lighten(c, 0.3), 1); g.fillTriangle(8, 7, 13, 8, 11, 12);
      g.fillStyle(darken(c, 0.15), 1); g.fillTriangle(8, 7, 8, 12, 3, 9);
      g.fillStyle(lighten(c, 0.15), 1); g.fillTriangle(8, 7, 3, 6, 5, 2);
      px(g, 8, 7, PAL.gold);
    },
    birdbath: (g, c) => {
      box(g, 6, 11, 4, 4, PAL.wallDark);
      box(g, 5, 14, 6, 1, PAL.wallDark);
      box(g, 4, 8, 8, 3, c);
      box(g, 5, 9, 6, 1, lighten(c, 0.4));
      box(g, 4, 8, 8, 1, darken(c, 0.2));
    },
    bush: (g, c) => {
      g.fillStyle(darken(c, 0.18), 1); g.fillCircle(8, 11, 6);
      g.fillStyle(c, 1); g.fillCircle(6, 9, 4); g.fillCircle(11, 9, 4); g.fillCircle(8, 7, 4);
      px(g, 5, 6, lighten(c, 0.3)); px(g, 10, 6, lighten(c, 0.3));
    },
    fence: (g, c) => {
      box(g, 0, 5, 16, 2, c); box(g, 0, 9, 16, 2, c); // rails span the tile so a row connects
      box(g, 0, 5, 16, 1, lighten(c, 0.2));
      box(g, 7, 3, 2, 11, darken(c, 0.15)); // post
      box(g, 7, 3, 2, 1, lighten(c, 0.25));
    },
    sprinkler: (g, c) => {
      box(g, 7, 8, 2, 6, 0x8a8e94); // post
      box(g, 5, 13, 6, 2, PAL.wood2); // base
      g.fillStyle(c, 1); g.fillCircle(8, 6, 3); // head
      px(g, 4, 5, c); px(g, 12, 5, c); px(g, 8, 2, c); px(g, 3, 8, c); px(g, 13, 8, c); // spray
    },
    copper_sprinkler: (g, c) => {
      box(g, 7, 8, 2, 6, darken(c, 0.3)); box(g, 5, 13, 6, 2, PAL.wood2);
      g.fillStyle(c, 1); g.fillCircle(8, 6, 3.5);
      px(g, 3, 5, c); px(g, 13, 5, c); px(g, 8, 1, c); px(g, 2, 8, c); px(g, 14, 8, c);
    },
    iron_sprinkler: (g, c) => {
      box(g, 7, 8, 2, 6, darken(c, 0.3)); box(g, 5, 13, 6, 2, PAL.wood2);
      g.fillStyle(c, 1); g.fillCircle(8, 6, 4);
      px(g, 2, 4, c); px(g, 14, 4, c); px(g, 8, 0, c); px(g, 1, 8, c); px(g, 15, 8, c); px(g, 8, 13, c);
    },
    path: (g, c) => {
      box(g, 0, 0, 16, 16, c); // opaque ground tile
      g.fillStyle(darken(c, 0.14), 1);
      g.fillCircle(4, 5, 2); g.fillCircle(11, 4, 2); g.fillCircle(8, 10, 2); g.fillCircle(13, 12, 2); g.fillCircle(3, 12, 1.5);
      g.fillStyle(lighten(c, 0.15), 1);
      g.fillCircle(4, 5, 1); g.fillCircle(8, 10, 1);
    },
    water_tile: (g, c) => {
      box(g, 0, 0, 16, 16, c); // water
      g.fillStyle(lighten(c, 0.28), 1); box(g, 0, 4, 16, 1, lighten(c, 0.28)); box(g, 0, 10, 16, 1, lighten(c, 0.28)); // ripples
      px(g, 3, 2, lighten(c, 0.45)); px(g, 11, 7, lighten(c, 0.45));
    },
    bridge: (g, c) => {
      box(g, 0, 4, 16, 8, c); // planks
      box(g, 0, 4, 16, 1, lighten(c, 0.2));
      for (let i = 1; i < 16; i += 3) box(g, i, 4, 1, 8, darken(c, 0.18)); // plank seams
      box(g, 0, 3, 16, 1, darken(c, 0.3)); box(g, 0, 12, 16, 1, darken(c, 0.3)); // rails
    },
    pet_bed: (g, c) => {
      g.fillStyle(c, 1); g.fillEllipse(8, 11, 14, 7); // base
      g.fillStyle(lighten(c, 0.35), 1); g.fillEllipse(8, 10, 9, 4); // cushion
      box(g, 1, 9, 14, 1, darken(c, 0.25));
      px(g, 6, 10, lighten(c, 0.5)); px(g, 10, 11, lighten(c, 0.5)); // paw marks
    },
    bee_hive: (g, c) => {
      g.fillStyle(c, 1); g.fillEllipse(8, 7, 11, 5); g.fillEllipse(8, 10, 12, 5); g.fillEllipse(8, 13, 11, 4); // hive
      box(g, 7, 12, 2, 2, darken(c, 0.4)); // entrance
      px(g, 3, 5, PAL.ink); px(g, 13, 7, PAL.ink); // bees
    },
    // ---- exclusive goal-reward decorations ----
    gnome: (g, c) => {
      box(g, 5, 9, 6, 6, 0xd8c4a8); // coat
      g.fillStyle(c, 1); g.fillTriangle(8, 1, 3, 9, 13, 9); // pointy hat
      box(g, 5, 12, 6, 3, PAL.white); // beard
      px(g, 6, 9, 0xf0c090); px(g, 9, 9, 0xf0c090); // cheeks
      px(g, 7, 9, PAL.ink); px(g, 8, 9, PAL.ink); // eyes
    },
    trophy: (g, c) => {
      g.fillStyle(c, 1); g.fillEllipse(8, 6, 8, 7); // bowl
      box(g, 3, 4, 2, 3, c); box(g, 11, 4, 2, 3, c); // handles
      box(g, 7, 9, 2, 3, darken(c, 0.2)); // stem
      box(g, 5, 12, 6, 3, PAL.wood2); // base
      px(g, 6, 4, lighten(c, 0.5)); // shine
    },
    wind_chime: (g, c) => {
      box(g, 4, 2, 8, 2, PAL.wood2); px(g, 8, 1, PAL.wood2); // top
      for (let i = 0; i < 4; i++) box(g, 4 + i * 2, 5, 1, 5 + (i % 2) * 2, c); // chimes
      box(g, 6, 12, 4, 2, lighten(c, 0.3)); // clapper
    },
    star_banner: (g, c) => {
      box(g, 7, 1, 1, 14, PAL.wood2); // pole
      g.fillStyle(c, 1); g.fillTriangle(8, 2, 15, 4, 8, 7); // pennant
      px(g, 11, 4, PAL.white); // star
      px(g, 8, 1, PAL.gold); // finial
    },
    golden_statue: (g, c) => {
      box(g, 4, 12, 8, 3, PAL.wood2); // pedestal
      g.fillStyle(c, 1); box(g, 6, 5, 4, 8, c); g.fillCircle(8, 4, 2.5); // figure
      box(g, 5, 7, 6, 1, darken(c, 0.2)); // arms
      px(g, 7, 4, lighten(c, 0.5)); // shine
    },
    fairy_lights: (g, c) => {
      box(g, 1, 3, 14, 1, PAL.wood2); // wire
      for (let i = 0; i < 6; i++) {
        g.fillStyle(i % 2 ? c : PAL.gold, 1);
        g.fillCircle(2 + i * 2.4, 5 + (i % 2) * 2, 1.4);
      }
    },
    weathervane: (g, c) => {
      box(g, 7, 5, 2, 10, PAL.wood2); // pole
      g.fillStyle(c, 1); g.fillTriangle(9, 3, 14, 5, 9, 7); // arrow head
      g.fillTriangle(7, 3, 2, 5, 7, 7); // tail
      px(g, 8, 5, PAL.gold);
    },
    fountain: (g, c) => {
      box(g, 3, 11, 10, 3, darken(c, 0.35)); // basin
      g.fillStyle(c, 1); g.fillEllipse(8, 11, 10, 3);
      box(g, 7, 5, 2, 6, darken(c, 0.2)); // column
      g.fillStyle(lighten(c, 0.45), 1);
      px(g, 8, 4, lighten(c, 0.45)); px(g, 6, 6, lighten(c, 0.45)); px(g, 10, 6, lighten(c, 0.45)); // spray
    },
    crystal: (g, c) => {
      g.fillStyle(c, 1); g.fillTriangle(8, 1, 4, 8, 12, 8); g.fillTriangle(4, 8, 12, 8, 8, 15);
      g.fillStyle(lighten(c, 0.4), 1); g.fillTriangle(8, 2, 6, 8, 10, 8);
      px(g, 7, 5, PAL.white); px(g, 9, 10, lighten(c, 0.3));
    },
    pumpkin_deco: (g, c) => {
      g.fillStyle(c, 1); g.fillEllipse(8, 11, 12, 9); // pumpkin
      g.fillStyle(darken(c, 0.25), 1); box(g, 5, 7, 1, 8, darken(c, 0.25)); box(g, 10, 7, 1, 8, darken(c, 0.25)); // ridges
      box(g, 7, 3, 2, 3, 0x5a7a2a); // stem
      g.fillStyle(0x3a2410, 1); g.fillTriangle(4, 11, 6, 11, 5, 9); g.fillTriangle(10, 11, 12, 11, 11, 9); // eyes
      box(g, 6, 13, 4, 1, 0x3a2410); // grin
    },
    snowman: (g, c) => {
      g.fillStyle(c, 1); g.fillCircle(8, 12, 4); g.fillCircle(8, 6, 3); // body + head
      box(g, 5, 3, 6, 1, 0x3a2a1e); box(g, 6, 1, 4, 2, 0x3a2a1e); // hat
      px(g, 7, 5, PAL.ink); px(g, 9, 5, PAL.ink); // eyes
      px(g, 8, 7, 0xe06020); // carrot nose
      px(g, 8, 11, PAL.ink); px(g, 8, 13, PAL.ink); // buttons
    },
  };

  for (const d of Object.values(DECORATIONS)) {
    const fn = draw[d.id];
    if (fn) {
      tex(scene, d.texture, d.w * TS, d.h * TS, (g) => fn(g, d.color));
    }
    // inventory icon: a tidy rounded chip tinted to the decoration
    tex(scene, d.icon, TS, TS, (g) => {
      g.fillStyle(darken(d.color, 0.1), 1);
      g.fillRoundedRect(2, 2, 12, 12, 3);
      g.fillStyle(lighten(d.color, 0.3), 1);
      g.fillRoundedRect(4, 4, 6, 5, 2);
    });
  }

  // growable plants: staged textures + a mature 'base' (shop preview / icon)
  for (const d of Object.values(DECORATIONS)) {
    if (!d.grows) continue;
    const stages = d.grows.stages;
    for (let s = 0; s < stages; s++) tex(scene, `${d.texture}_${s}`, TS, TS, (g) => drawTree(g, s, stages));
    tex(scene, d.texture, TS, TS, (g) => drawTree(g, stages - 1, stages));
  }
}

// ---- crops (one texture per growth stage) -------------------------------------

function drawCrop(g: G, def: CropDef, stage: number): void {
  const last = Math.max(1, def.stages - 1);
  if (stage <= 0) {
    box(g, 7, 12, 1, 3, PAL.leaf1);
    box(g, 8, 11, 1, 4, PAL.leaf1);
    px(g, 6, 12, PAL.leaf2);
    px(g, 9, 11, PAL.leaf2);
    return;
  }
  const t = stage / last;
  const baseY = 15;
  const height = Math.round(3 + t * 9);
  const top = baseY - height;
  box(g, 7, top, 2, height, PAL.leaf2); // stem
  box(g, 5, top + 2, 2, 1, PAL.leaf1);
  box(g, 9, top + 3, 2, 1, PAL.leaf1);
  box(g, 4, top + 5, 2, 1, PAL.leaf1);
  if (stage >= last) {
    g.fillStyle(def.color, 1);
    g.fillCircle(8, top + 1, 3);
    g.fillStyle(lighten(def.color, 0.35), 1);
    g.fillRect(6, top - 1, 1, 1);
    g.fillStyle(def.color, 1);
    g.fillCircle(10, baseY - 3, 2);
  }
}

/** Growable tree at a stage (0=sprout … last=mature with fruit). */
function drawTree(g: G, stage: number, stages: number): void {
  const mature = stage >= stages - 1;
  const trunkH = stage === 0 ? 3 : stage === 1 ? 5 : 7;
  box(g, 7, 16 - trunkH, 2, trunkH, PAL.wood2);
  const r = stage === 0 ? 3 : stage === 1 ? 5 : 6;
  const cy = 16 - trunkH - r + 2;
  g.fillStyle(PAL.leaf2, 1);
  g.fillCircle(8, cy, r);
  g.fillStyle(PAL.leaf1, 1);
  g.fillCircle(7, cy - 1, r - 1);
  px(g, 6, cy - 2, lighten(PAL.leaf1, 0.3));
  if (mature) {
    g.fillStyle(0xe0443a, 1);
    g.fillCircle(5, cy, 1.5);
    g.fillCircle(10, cy + 1, 1.5);
    g.fillCircle(8, cy - 2, 1.5);
  }
}

function buildCrops(scene: Phaser.Scene): void {
  for (const def of Object.values(CROPS)) {
    for (let s = 0; s < def.stages; s++) {
      tex(scene, cropTextureKey(def.id, s), TS, TS, (g) => drawCrop(g, def, s));
    }
  }
}

// ---- item icons (seeds + produce) ---------------------------------------------

function buildItemIcons(scene: Phaser.Scene): void {
  for (const def of Object.values(CROPS)) {
    // seed packet
    tex(scene, itemIconKey(def.seedItemId), TS, TS, (g) => {
      box(g, 3, 3, 10, 11, PAL.wall);
      box(g, 3, 3, 10, 2, PAL.wallDark);
      g.fillStyle(def.color, 1); g.fillCircle(8, 9, 2);
      px(g, 6, 11, PAL.ink); px(g, 9, 11, PAL.ink);
    });
    // produce
    tex(scene, itemIconKey(def.produceItemId), TS, TS, (g) => {
      g.fillStyle(def.color, 1); g.fillCircle(8, 9, 5);
      g.fillStyle(lighten(def.color, 0.35), 1); g.fillCircle(6, 7, 1.5);
      box(g, 7, 2, 2, 3, PAL.leaf1); px(g, 9, 3, PAL.leaf2);
    });
  }
  // material: wood (from chopping trees)
  tex(scene, itemIconKey('wood'), TS, TS, (g) => {
    box(g, 3, 7, 10, 5, PAL.wood1);
    box(g, 3, 7, 10, 1, lighten(PAL.wood1, 0.2));
    box(g, 3, 11, 10, 1, PAL.wood2);
    g.fillStyle(lighten(PAL.wood1, 0.35), 1); g.fillCircle(4, 9, 1.6);
    px(g, 4, 9, PAL.wood2);
  });
  tex(scene, itemIconKey('apple'), TS, TS, (g) => {
    g.fillStyle(0xe0443a, 1); g.fillCircle(8, 9, 5);
    g.fillStyle(lighten(0xe0443a, 0.4), 1); g.fillCircle(6, 7, 1.5);
    box(g, 7, 2, 2, 3, PAL.leaf1); px(g, 9, 3, PAL.leaf2);
  });
  tex(scene, itemIconKey('fish'), TS, TS, (g) => {
    g.fillStyle(0x6fb0c8, 1); g.fillEllipse(7, 9, 9, 6);
    g.fillTriangle(11, 9, 15, 6, 15, 12);
    px(g, 5, 8, PAL.ink);
    g.fillStyle(lighten(0x6fb0c8, 0.3), 1); px(g, 7, 7, lighten(0x6fb0c8, 0.3));
  });
  tex(scene, itemIconKey('fish_carp'), TS, TS, (g) => {
    g.fillStyle(0xc89858, 1); g.fillEllipse(7, 9, 9, 6);
    g.fillTriangle(11, 9, 15, 6, 15, 12);
    px(g, 5, 8, PAL.ink);
    g.fillStyle(lighten(0xc89858, 0.3), 1); px(g, 7, 7, lighten(0xc89858, 0.3));
  });
  tex(scene, itemIconKey('fish_perch'), TS, TS, (g) => {
    g.fillStyle(0x7aa84a, 1); g.fillEllipse(7, 9, 9, 6);
    g.fillTriangle(11, 9, 15, 6, 15, 12);
    px(g, 5, 8, PAL.ink);
    px(g, 7, 8, 0x4a6a2e); px(g, 9, 9, 0x4a6a2e);
  });
  tex(scene, itemIconKey('fish_bass'), TS, TS, (g) => {
    g.fillStyle(0x6a9a5a, 1); g.fillEllipse(7, 9, 10, 6); // green bass
    g.fillTriangle(12, 9, 15, 6, 15, 12);
    px(g, 5, 8, PAL.ink);
    px(g, 7, 7, darken(0x6a9a5a, 0.2)); px(g, 9, 10, darken(0x6a9a5a, 0.2));
  });
  tex(scene, itemIconKey('fish_trout'), TS, TS, (g) => {
    g.fillStyle(0xd87a8c, 1); g.fillEllipse(7, 9, 10, 6); // pink trout
    g.fillTriangle(12, 9, 15, 6, 15, 12);
    px(g, 5, 8, PAL.ink);
    px(g, 6, 8, PAL.white); px(g, 8, 7, PAL.white); px(g, 9, 10, PAL.white); // spots
  });
  tex(scene, itemIconKey('milk'), TS, TS, (g) => {
    box(g, 5, 4, 6, 10, PAL.white); // bottle
    box(g, 5, 4, 6, 2, 0xdfe6ea);
    box(g, 6, 2, 4, 2, PAL.wallDark); // cap
    box(g, 5, 9, 6, 1, 0x9fcbe0); // label
  });
  tex(scene, itemIconKey('wool'), TS, TS, (g) => {
    g.fillStyle(0xf2f0ea, 1);
    g.fillCircle(6, 8, 4); g.fillCircle(10, 8, 4); g.fillCircle(8, 6, 4); g.fillCircle(8, 10, 4);
    px(g, 6, 8, 0xded6cc); px(g, 10, 9, 0xded6cc); px(g, 8, 7, 0xded6cc);
  });
  tex(scene, itemIconKey('truffle'), TS, TS, (g) => {
    g.fillStyle(0x4a3a2e, 1); g.fillCircle(8, 9, 5);
    g.fillStyle(0x5c4838, 1); g.fillCircle(6, 7, 2); g.fillCircle(10, 8, 2);
    px(g, 7, 8, 0x2e241c); px(g, 10, 10, 0x2e241c); px(g, 6, 11, 0x2e241c);
    px(g, 9, 6, 0x6e5848);
  });
  tex(scene, itemIconKey('duck_egg'), TS, TS, (g) => {
    g.fillStyle(0xdfeae0, 1); g.fillEllipse(8, 9, 10, 13);
    g.fillStyle(0xffffff, 0.6); g.fillCircle(6, 6, 2);
  });
  const dish = (id: string, food: number, garnish: number): void =>
    tex(scene, itemIconKey(id), TS, TS, (g) => {
      g.fillStyle(0xeae6df, 1); g.fillEllipse(8, 11, 13, 5); // plate
      g.fillStyle(0xffffff, 1); g.fillEllipse(8, 10, 9, 3);
      g.fillStyle(food, 1); g.fillEllipse(8, 8, 8, 5); // food mound
      g.fillStyle(garnish, 1); px(g, 6, 7, garnish); px(g, 10, 8, garnish); px(g, 8, 6, garnish); // garnish
    });
  dish('omelette', 0xf2d24a, 0xe0a13a);
  dish('garden_salad', 0x6ab04a, 0xe0443a);
  dish('fruit_bowl', 0xe06d8b, 0xf2d24a);
  dish('veggie_stew', 0xb0703a, 0x6ab04a);
  dish('pumpkin_pie', 0xe08a3c, 0xfff4e0);
  dish('fish_dinner', 0x6fb0c8, 0xf2d24a);
  tex(scene, itemIconKey('berry'), TS, TS, (g) => {
    g.fillStyle(0xc0445a, 1); g.fillCircle(6, 9, 3); g.fillCircle(10, 9, 3); g.fillCircle(8, 6, 3);
    px(g, 5, 8, lighten(0xc0445a, 0.3)); px(g, 9, 8, lighten(0xc0445a, 0.3));
    g.fillStyle(PAL.leaf1, 1); px(g, 8, 4, PAL.leaf1);
  });
  tex(scene, itemIconKey('mushroom'), TS, TS, (g) => {
    box(g, 7, 9, 2, 5, 0xeae0d0); g.fillStyle(0xc0443a, 1); g.fillEllipse(8, 8, 9, 5);
    px(g, 6, 7, PAL.white); px(g, 10, 8, PAL.white);
  });
  tex(scene, itemIconKey('herb'), TS, TS, (g) => {
    box(g, 7, 8, 2, 6, 0x3a6a2a); g.fillStyle(0x6ab04a, 1);
    g.fillEllipse(5, 8, 5, 2); g.fillEllipse(11, 8, 5, 2); g.fillEllipse(8, 5, 4, 3);
  });
  tex(scene, itemIconKey('shell'), TS, TS, (g) => {
    g.fillStyle(0xf0c8b0, 1); g.fillTriangle(8, 3, 3, 13, 13, 13);
    box(g, 7, 6, 2, 7, 0xe0a890); box(g, 5, 8, 1, 5, 0xe0a890); box(g, 10, 8, 1, 5, 0xe0a890);
  });
  tex(scene, itemIconKey('starfish'), TS, TS, (g) => {
    g.fillStyle(0xe08a4a, 1);
    g.fillTriangle(8, 2, 6, 8, 10, 8); g.fillTriangle(2, 7, 8, 9, 6, 13); g.fillTriangle(14, 7, 10, 13, 8, 9);
    g.fillTriangle(4, 14, 8, 8, 8, 14); g.fillTriangle(12, 14, 8, 14, 8, 8);
    g.fillStyle(0xf0b070, 1); g.fillCircle(8, 8, 2);
  });
  tex(scene, itemIconKey('seaweed'), TS, TS, (g) => {
    box(g, 5, 5, 2, 10, 0x3a8a4a); box(g, 9, 3, 2, 12, 0x3a8a4a);
    g.fillStyle(0x4faa5a, 1); px(g, 4, 7, 0x4faa5a); px(g, 11, 5, 0x4faa5a);
  });
  tex(scene, itemIconKey('crab'), TS, TS, (g) => {
    g.fillStyle(0xd0563a, 1); g.fillEllipse(8, 9, 9, 6);
    box(g, 2, 8, 2, 1, 0xd0563a); box(g, 12, 8, 2, 1, 0xd0563a);
    px(g, 3, 7, 0xb0402a); px(g, 13, 7, 0xb0402a);
    px(g, 6, 7, PAL.ink); px(g, 10, 7, PAL.ink);
  });
  tex(scene, itemIconKey('squid'), TS, TS, (g) => {
    g.fillStyle(0xc080a8, 1); g.fillEllipse(8, 6, 6, 7);
    box(g, 5, 10, 1, 4, 0xc080a8); box(g, 7, 10, 1, 4, 0xc080a8); box(g, 9, 10, 1, 4, 0xc080a8); box(g, 11, 10, 1, 4, 0xc080a8);
    px(g, 6, 5, PAL.ink); px(g, 10, 5, PAL.ink);
  });
  tex(scene, itemIconKey('clam'), TS, TS, (g) => {
    g.fillStyle(0xe8c8b0, 1); g.fillEllipse(8, 8, 11, 7);
    g.fillStyle(0xd0a890, 1); box(g, 4, 5, 1, 6, 0xd0a890); box(g, 7, 5, 1, 6, 0xd0a890); box(g, 10, 5, 1, 6, 0xd0a890);
    g.fillStyle(0xfff0e0, 1); box(g, 5, 10, 6, 1, 0xfff0e0);
  });
  tex(scene, itemIconKey('butterfly'), TS, TS, (g) => {
    box(g, 7, 4, 2, 8, PAL.ink);
    g.fillStyle(0xe06d8b, 1); g.fillEllipse(4, 6, 6, 5); g.fillEllipse(12, 6, 6, 5);
    px(g, 4, 6, 0xf2c14e); px(g, 12, 6, 0xf2c14e);
  });
  tex(scene, itemIconKey('beetle'), TS, TS, (g) => {
    g.fillStyle(0x3a6a4a, 1); g.fillEllipse(8, 9, 8, 9);
    box(g, 8, 4, 1, 11, 0x2a4a36); px(g, 8, 3, PAL.ink);
    px(g, 6, 6, 0x6fb04a); px(g, 10, 7, 0x6fb04a);
  });
  tex(scene, itemIconKey('stone'), TS, TS, (g) => {
    g.fillStyle(0x8a8694, 1); g.fillEllipse(8, 9, 10, 7);
    px(g, 6, 7, 0x6a6678); px(g, 10, 10, 0x6a6678); px(g, 9, 6, 0xa8a4b0);
  });
  tex(scene, itemIconKey('coal'), TS, TS, (g) => {
    g.fillStyle(0x2c2a32, 1); g.fillEllipse(8, 9, 10, 7);
    px(g, 6, 7, 0x44414c); px(g, 10, 9, 0x44414c); px(g, 9, 6, 0x6a6678);
  });
  tex(scene, itemIconKey('copper_ore'), TS, TS, (g) => {
    g.fillStyle(0x8a8694, 1); g.fillEllipse(8, 9, 10, 7);
    px(g, 6, 8, 0xc97f4a); px(g, 9, 7, 0xc97f4a); px(g, 10, 10, 0xc97f4a);
  });
  tex(scene, itemIconKey('iron_ore'), TS, TS, (g) => {
    g.fillStyle(0x8a8694, 1); g.fillEllipse(8, 9, 10, 7);
    px(g, 6, 8, 0xb8a89a); px(g, 9, 7, 0xb8a89a); px(g, 10, 10, 0xb8a89a);
  });
  tex(scene, itemIconKey('gem'), TS, TS, (g) => {
    g.fillStyle(0x5ad0e0, 1); g.fillTriangle(8, 2, 3, 7, 13, 7); g.fillTriangle(3, 7, 13, 7, 8, 14);
    g.fillStyle(0x9ff0ff, 1); g.fillTriangle(8, 3, 6, 7, 10, 7);
    px(g, 7, 5, PAL.white);
  });
  tex(scene, itemIconKey('jewelry'), TS, TS, (g) => {
    g.lineStyle(2, PAL.gold, 1); g.strokeCircle(8, 11, 4); // gold ring
    g.fillStyle(0x5ad0e0, 1); g.fillTriangle(8, 2, 5, 6, 11, 6); g.fillTriangle(5, 6, 11, 6, 8, 9); // set gem
    px(g, 8, 4, PAL.white);
  });
  tex(scene, itemIconKey('cake'), TS, TS, (g) => {
    box(g, 3, 8, 10, 6, 0xf4d8e0); // cake
    box(g, 3, 7, 10, 2, 0xffffff); // frosting
    box(g, 7, 3, 2, 4, 0xf2c14e); // candle
    g.fillStyle(0xe0503a, 1); g.fillCircle(8, 3, 1.5); // flame
    px(g, 5, 10, 0xe06d8b); px(g, 10, 11, 0x5ab0d8); // sprinkles
  });
  tex(scene, itemIconKey('copper_bar'), TS, TS, (g) => {
    box(g, 3, 7, 10, 5, 0xc97f4a);
    box(g, 3, 7, 10, 1, 0xe09a64); box(g, 3, 11, 10, 1, 0xa05f34);
  });
  tex(scene, itemIconKey('iron_bar'), TS, TS, (g) => {
    box(g, 3, 7, 10, 5, 0xb8b0a8);
    box(g, 3, 7, 10, 1, 0xd8d0c8); box(g, 3, 11, 10, 1, 0x908880);
  });
  tex(scene, itemIconKey('honey'), TS, TS, (g) => {
    box(g, 5, 5, 6, 9, 0xf2c14e); // jar
    box(g, 5, 5, 6, 2, 0xe0a13a);
    box(g, 6, 3, 4, 2, PAL.wood2); // lid
    px(g, 7, 9, lighten(0xf2c14e, 0.3));
  });
  tex(scene, itemIconKey('fertilizer'), TS, TS, (g) => {
    box(g, 4, 5, 8, 9, 0x7a5a3a); // sack
    box(g, 4, 5, 8, 2, 0x9a7a4a);
    px(g, 7, 9, 0x6fb04a); px(g, 9, 10, 0x6fb04a); px(g, 6, 11, 0x8fce5a); // pellets
  });
}

function buildForest(scene: Phaser.Scene): void {
  tex(scene, TX.berryBush, TS, TS, (g) => {
    g.fillStyle(0x3f7d36, 1); g.fillCircle(8, 10, 6); g.fillCircle(5, 8, 4); g.fillCircle(11, 8, 4);
    g.fillStyle(0xc0445a, 1); px(g, 6, 9, 0xc0445a); px(g, 10, 8, 0xc0445a); px(g, 8, 11, 0xc0445a); px(g, 9, 6, 0xc0445a);
  });
  tex(scene, TX.forageMushroom, TS, TS, (g) => {
    box(g, 6, 9, 2, 5, 0xeae0d0); g.fillStyle(0xc0443a, 1); g.fillEllipse(7, 8, 8, 5);
    px(g, 5, 7, PAL.white); px(g, 9, 8, PAL.white);
    box(g, 10, 11, 2, 3, 0xeae0d0); g.fillStyle(0xd08a3a, 1); g.fillEllipse(11, 10, 5, 3);
  });
  tex(scene, TX.herbPlant, TS, TS, (g) => {
    box(g, 7, 8, 2, 6, 0x3a6a2a); g.fillStyle(0x6ab04a, 1);
    g.fillEllipse(5, 8, 5, 2); g.fillEllipse(11, 8, 5, 2); g.fillEllipse(8, 5, 4, 3);
    px(g, 8, 4, 0x9fd06a);
  });
  tex(scene, TX.stump, TS, TS, (g) => {
    box(g, 4, 8, 8, 6, PAL.wood2); // trunk
    g.fillStyle(PAL.wood1, 1); g.fillEllipse(8, 8, 8, 4); // rings top
    g.fillStyle(darken(PAL.wood1, 0.2), 1); g.fillEllipse(8, 8, 4, 2);
    box(g, 5, 13, 6, 2, darken(PAL.wood2, 0.2));
  });
  tex(scene, TX.forestGround, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, 0x3c5a2e); // forest grass
    px(g, 4, 5, 0x32502a); px(g, 11, 8, 0x32502a); px(g, 7, 12, 0x47683a); px(g, 2, 11, 0x47683a);
  });
  tex(scene, TX.townGround, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, 0x86c060); // village green
    px(g, 3, 4, 0x76b052); px(g, 11, 9, 0x76b052); px(g, 7, 12, 0x9ad078); px(g, 13, 3, 0x9ad078);
  });
  tex(scene, TX.townPath, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, 0xc2a878); // cobblestone path
    px(g, 3, 3, 0xb09866); px(g, 9, 5, 0xb09866); px(g, 6, 10, 0xd4bc8e); px(g, 12, 12, 0xb09866);
  });
  const townie = (key: string, shirt: number, hair: number): void =>
    tex(scene, key, TS, TS, (g) => {
      box(g, 5, 9, 6, 5, shirt); // body
      g.fillStyle(0xf0c8a0, 1); g.fillCircle(8, 6, 3); // head
      box(g, 5, 3, 6, 2, hair); px(g, 5, 5, hair); px(g, 10, 5, hair); // hair
      px(g, 7, 6, 0x2a2a2a); px(g, 9, 6, 0x2a2a2a); // eyes
      box(g, 5, 14, 2, 2, 0x4a3a2e); box(g, 9, 14, 2, 2, 0x4a3a2e); // shoes
    });
  townie(TX.npcA, 0xd06a6a, 0x6a4a2e);
  townie(TX.npcB, 0x6a8ad0, 0xe0c060);
  townie(TX.npcC, 0x6ab06a, 0x3a2a2a);
  tex(scene, TX.booth, TS, TS, (g) => {
    box(g, 2, 8, 12, 7, 0xc9a878); // counter
    box(g, 2, 6, 12, 1, 0x8a6a4a);
    box(g, 1, 2, 14, 4, 0xd0443a); // awning
    box(g, 4, 2, 3, 4, 0xf2f0ea); box(g, 10, 2, 3, 4, 0xf2f0ea); // stripes
    px(g, 8, 11, 0xf2c14e); // target dot
  });
  tex(scene, TX.mole, TS, TS, (g) => {
    g.fillStyle(0x8a6a4a, 1); g.fillEllipse(8, 9, 11, 10); // body
    g.fillStyle(0xb89878, 1); g.fillEllipse(8, 11, 7, 5); // belly
    px(g, 6, 7, 0x2a2a2a); px(g, 10, 7, 0x2a2a2a); // eyes
    px(g, 8, 9, 0xe0a0a0); // nose
    px(g, 4, 4, 0x6a4a2e); px(g, 12, 4, 0x6a4a2e); // ears
  });
  tex(scene, TX.vendorCart, 24, 18, (g) => {
    box(g, 2, 6, 20, 7, 0x9a6b3f); // cart body
    box(g, 2, 6, 20, 1, 0xb88a5a);
    box(g, 1, 1, 22, 4, 0xd0443a); // canopy
    box(g, 4, 1, 3, 4, 0xf2f0ea); box(g, 11, 1, 3, 4, 0xf2f0ea); box(g, 18, 1, 3, 4, 0xf2f0ea); // stripes
    box(g, 6, 8, 3, 3, 0xf2c14e); box(g, 12, 8, 3, 3, 0xe05a4a); box(g, 16, 8, 3, 3, 0x5ab0d8); // goods
    g.fillStyle(0x2a2a2a, 1); g.fillCircle(6, 15, 3); g.fillCircle(18, 15, 3); // wheels
    px(g, 6, 15, 0x6a6a6a); px(g, 18, 15, 0x6a6a6a);
  });
  tex(scene, TX.well, TS, TS, (g) => {
    box(g, 3, 8, 10, 6, 0x9aa0a8); // stone base
    box(g, 3, 8, 10, 1, 0x7e848c);
    box(g, 4, 9, 8, 4, 0x2a3a4a); // shaft
    box(g, 5, 10, 6, 2, 0x4a90c2); // water
    box(g, 3, 3, 1, 6, PAL.wood2); box(g, 12, 3, 1, 6, PAL.wood2); // posts
    g.fillStyle(PAL.roof, 1); g.fillTriangle(2, 4, 8, 0, 14, 4); // little roof
  });
  tex(scene, TX.forestTree, 16, 22, (g) => {
    box(g, 6, 14, 4, 8, PAL.wood2); // trunk
    g.fillStyle(0x2f5a26, 1); g.fillCircle(8, 8, 7); g.fillCircle(4, 11, 4); g.fillCircle(12, 11, 4); // canopy
    g.fillStyle(0x46823a, 1); g.fillCircle(6, 6, 3); g.fillCircle(10, 9, 3);
    px(g, 7, 4, 0x6fb04a); px(g, 9, 7, 0x6fb04a);
  });
  tex(scene, TX.treefort, 24, 28, (g) => {
    box(g, 10, 18, 5, 10, PAL.wood2); // trunk
    g.fillStyle(0x2f5a26, 1); g.fillCircle(12, 20, 6); g.fillCircle(5, 16, 4); g.fillCircle(19, 16, 4); // lower leaves
    box(g, 6, 7, 12, 9, PAL.wood1); box(g, 6, 7, 12, 1, lighten(PAL.wood1, 0.2)); // fort
    for (let i = 7; i < 18; i += 3) box(g, i, 8, 1, 8, darken(PAL.wood1, 0.15));
    g.fillStyle(0x6a4a2e, 1); g.fillTriangle(4, 8, 20, 8, 12, 2); // roof
    box(g, 10, 11, 4, 5, 0x3a2a1e); // doorway
    px(g, 8, 11, 0xf2c14e); px(g, 15, 11, 0xf2c14e); // window glow
  });
}

function buildBeach(scene: Phaser.Scene): void {
  tex(scene, TX.sand, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, 0xe8d9a8);
    px(g, 4, 5, 0xd8c690); px(g, 11, 9, 0xd8c690); px(g, 7, 12, 0xf0e4bc); px(g, 2, 10, 0xf0e4bc);
  });
  tex(scene, TX.ocean, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, 0x3f8fc0);
    box(g, 0, 4, TS, 1, 0x67b0d8); box(g, 0, 10, TS, 1, 0x67b0d8); // ripples
    px(g, 3, 2, 0x9fd0e8); px(g, 11, 7, 0x9fd0e8);
  });
  tex(scene, TX.shell, TS, TS, (g) => {
    g.fillStyle(0xf0c8b0, 1); g.fillTriangle(8, 3, 3, 13, 13, 13);
    box(g, 7, 5, 2, 8, 0xe0a890); box(g, 5, 7, 1, 6, 0xe0a890); box(g, 10, 7, 1, 6, 0xe0a890);
    px(g, 8, 12, 0xc08878);
  });
  tex(scene, TX.starfish, TS, TS, (g) => {
    g.fillStyle(0xe08a4a, 1);
    g.fillTriangle(8, 2, 6, 8, 10, 8); g.fillTriangle(2, 7, 8, 9, 6, 13); g.fillTriangle(14, 7, 10, 13, 8, 9);
    g.fillTriangle(4, 14, 8, 8, 8, 14); g.fillTriangle(12, 14, 8, 14, 8, 8);
    g.fillStyle(0xf0b070, 1); g.fillCircle(8, 8, 2);
    px(g, 7, 7, 0xc06a2a); px(g, 9, 9, 0xc06a2a);
  });
  tex(scene, TX.seaweed, TS, TS, (g) => {
    box(g, 5, 6, 2, 9, 0x3a8a4a); box(g, 9, 4, 2, 11, 0x3a8a4a);
    g.fillStyle(0x4faa5a, 1); px(g, 4, 8, 0x4faa5a); px(g, 11, 6, 0x4faa5a); px(g, 6, 5, 0x4faa5a);
  });
  tex(scene, TX.butterfly, 12, 10, (g) => {
    box(g, 5, 2, 2, 6, PAL.ink); // body
    g.fillStyle(0xe06d8b, 1); g.fillEllipse(3, 3, 4, 4); g.fillEllipse(9, 3, 4, 4); // upper wings
    g.fillStyle(0xf2a0b8, 1); g.fillEllipse(3, 7, 3, 3); g.fillEllipse(9, 7, 3, 3); // lower wings
    px(g, 4, 1, PAL.ink); px(g, 7, 1, PAL.ink); // antennae
  });
}

function buildMine(scene: Phaser.Scene): void {
  tex(scene, TX.mineFloor, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, 0x3a3640);
    px(g, 4, 5, 0x46424e); px(g, 11, 9, 0x46424e); px(g, 7, 12, 0x322e38);
  });
  tex(scene, TX.mineRock, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, 0x55505e);
    g.fillStyle(0x46424e, 1); g.fillTriangle(0, 0, 8, 6, 0, 12);
    px(g, 10, 4, 0x666070); px(g, 5, 9, 0x666070);
    box(g, 0, 14, TS, 2, 0x2a2832);
  });
  tex(scene, TX.oreRock, TS, TS, (g) => {
    g.fillStyle(0x55505e, 1); g.fillEllipse(8, 9, 12, 9);
    g.fillStyle(0x6a6478, 1); g.fillEllipse(6, 7, 5, 4);
    px(g, 9, 8, 0xf2d24a); px(g, 11, 10, 0xf2d24a); px(g, 7, 11, 0x6fb0d8); // ore flecks
  });
  tex(scene, TX.ladder, TS, TS, (g) => {
    box(g, 4, 0, 2, 16, PAL.wood2); box(g, 10, 0, 2, 16, PAL.wood2);
    for (let i = 2; i < 16; i += 4) box(g, 4, i, 8, 2, PAL.wood1);
  });
}

// ---- UI / FX ------------------------------------------------------------------

function buildUi(scene: Phaser.Scene): void {
  tex(scene, TX.heart, TS, TS, (g) => {
    g.fillStyle(PAL.red, 1);
    g.fillCircle(5, 6, 3);
    g.fillCircle(11, 6, 3);
    g.fillTriangle(2, 7, 14, 7, 8, 14);
    px(g, 4, 4, PAL.white);
  });

  tex(scene, TX.coin, TS, TS, (g) => {
    g.fillStyle(PAL.goldDark, 1); g.fillCircle(8, 8, 6);
    g.fillStyle(PAL.gold, 1); g.fillCircle(8, 8, 5);
    g.fillStyle(lighten(PAL.gold, 0.4), 1); g.fillCircle(6, 6, 1.5);
    px(g, 8, 5, PAL.goldDark); px(g, 8, 11, PAL.goldDark);
  });

  tex(scene, TX.energyPip, TS, TS, (g) => {
    g.fillStyle(PAL.leaf2, 1); g.fillCircle(8, 8, 5);
    g.fillStyle(PAL.leaf1, 1); g.fillCircle(8, 8, 4);
    box(g, 7, 8, 1, 5, PAL.leaf2); // stem/vein
    px(g, 6, 6, lighten(PAL.leaf1, 0.4));
  });

  tex(scene, TX.moveMarker, TS, TS, (g) => {
    g.lineStyle(2, PAL.white, 0.95);
    g.strokeCircle(8, 8, 5);
    g.fillStyle(PAL.white, 0.9);
    g.fillCircle(8, 8, 1.5);
  });

  tex(scene, TX.uiSlot, 20, 20, (g) => {
    g.fillStyle(PAL.ink, 0.35); g.fillRoundedRect(0, 0, 20, 20, 4);
    g.lineStyle(1, lighten(PAL.ink, 0.4), 0.8); g.strokeRoundedRect(0.5, 0.5, 19, 19, 4);
  });

  tex(scene, TX.uiSlotActive, 20, 20, (g) => {
    g.fillStyle(PAL.ink, 0.45); g.fillRoundedRect(0, 0, 20, 20, 4);
    g.lineStyle(2, PAL.gold, 1); g.strokeRoundedRect(1, 1, 18, 18, 4);
  });
}

// ---- tools + tier-2 world objects ---------------------------------------------

function buildTools(scene: Phaser.Scene): void {
  tex(scene, TX.toolHand, TS, TS, (g) => {
    box(g, 5, 6, 6, 5, PAL.skin);
    box(g, 5, 4, 1, 4, PAL.skin); box(g, 7, 3, 1, 5, PAL.skin); box(g, 9, 4, 1, 4, PAL.skin);
    box(g, 6, 11, 4, 3, PAL.skin);
  });
  tex(scene, TX.toolHoe, TS, TS, (g) => {
    box(g, 6, 3, 2, 11, PAL.wood1);
    box(g, 4, 2, 6, 2, 0x9aa0a8);
  });
  tex(scene, TX.toolWater, TS, TS, (g) => {
    box(g, 4, 7, 8, 6, 0x6fa8c8); box(g, 3, 6, 10, 2, 0x5a93b4);
    box(g, 12, 5, 3, 2, 0x5a93b4); box(g, 1, 7, 2, 3, 0x6fa8c8);
    px(g, 6, 4, PAL.water2); px(g, 7, 3, PAL.water2);
  });
  tex(scene, TX.toolAxe, TS, TS, (g) => {
    box(g, 7, 3, 2, 11, PAL.wood1);
    g.fillStyle(0x9aa0a8, 1); g.fillTriangle(8, 3, 13, 4, 8, 7);
  });
  tex(scene, TX.toolScythe, TS, TS, (g) => {
    box(g, 6, 4, 2, 10, PAL.wood1);
    g.fillStyle(0xcfd6e0, 1); g.fillTriangle(7, 4, 13, 3, 12, 7);
  });
  tex(scene, TX.toolMove, TS, TS, (g) => {
    g.fillStyle(0xcfd6e0, 1);
    g.fillTriangle(8, 1, 5, 4, 11, 4);
    g.fillTriangle(8, 15, 5, 12, 11, 12);
    g.fillTriangle(1, 8, 4, 5, 4, 11);
    g.fillTriangle(15, 8, 12, 5, 12, 11);
    box(g, 7, 4, 2, 8, 0xcfd6e0); box(g, 4, 7, 8, 2, 0xcfd6e0);
  });
  tex(scene, TX.toolRod, TS, TS, (g) => {
    box(g, 3, 2, 2, 12, PAL.wood1);
    box(g, 4, 2, 8, 1, PAL.wood2);
    box(g, 11, 3, 1, 6, 0xcfd6e0);
    g.fillStyle(PAL.white, 1); g.fillCircle(11, 10, 1.6);
    g.fillStyle(PAL.red, 1); g.fillCircle(11, 9, 1);
  });
}

function buildAmbient(scene: Phaser.Scene): void {
  tex(scene, TX.cloud, 32, 16, (g) => {
    g.fillStyle(0xffffff, 0.92);
    g.fillCircle(9, 10, 5); g.fillCircle(16, 8, 6); g.fillCircle(23, 10, 5); g.fillCircle(16, 12, 6);
    g.fillStyle(0xeaf2ff, 0.92); g.fillCircle(12, 12, 4); g.fillCircle(21, 12, 4);
  });
  tex(scene, TX.smoke, 8, 8, (g) => {
    g.fillStyle(0xd8d2c6, 0.85); g.fillCircle(4, 4, 3);
    g.fillStyle(0xeae6dc, 0.85); g.fillCircle(5, 3, 1.6);
  });
  tex(scene, TX.bobber, 8, 8, (g) => {
    g.fillStyle(PAL.red, 1); g.fillCircle(4, 3, 3);
    g.fillStyle(PAL.white, 1); g.fillCircle(4, 5, 3);
    box(g, 3, 6, 2, 2, PAL.red);
  });
  tex(scene, TX.ray, 32, 140, (g) => {
    g.fillGradientStyle(0xffffff, 0xffffff, 0xffffff, 0xffffff, 0.45, 0.45, 0, 0);
    g.fillRect(0, 0, 32, 140);
  });
  tex(scene, TX.raindrop, 2, 8, (g) => {
    g.fillStyle(0xbfe3f0, 0.9);
    g.fillRect(0, 0, 2, 8);
  });
  tex(scene, TX.snowflake, 4, 4, (g) => {
    g.fillStyle(0xffffff, 0.95); g.fillCircle(2, 2, 1.6);
  });
  tex(scene, TX.leaf, 6, 5, (g) => {
    g.fillStyle(0xd07a2a, 1); g.fillEllipse(3, 2.5, 5, 3);
    box(g, 3, 1, 1, 3, 0x8a4a18); px(g, 1, 2, 0xb05a1a);
  });
  tex(scene, TX.dust, 4, 4, (g) => {
    g.fillStyle(0xb89a72, 0.9); g.fillCircle(2, 2, 1.8);
  });
  tex(scene, TX.sparkle, 5, 5, (g) => {
    g.fillStyle(0xfff4b0, 1); box(g, 2, 0, 1, 5, 0xfff4b0); box(g, 0, 2, 5, 1, 0xfff4b0);
    g.fillStyle(0xffffff, 1); px(g, 2, 2, 0xffffff);
  });
  tex(scene, TX.firefly, 5, 5, (g) => {
    g.fillStyle(0xf2f0a0, 0.9); g.fillCircle(2.5, 2.5, 2.2);
    g.fillStyle(0xffffe0, 1); g.fillCircle(2.5, 2.5, 1.1);
  });
  tex(scene, TX.shootingstar, 18, 6, (g) => {
    g.fillStyle(0xfff0a0, 0.55); g.fillTriangle(15, 2, 15, 4, 0, 3); // tail
    g.fillStyle(0xffffff, 1); g.fillCircle(15, 3, 2.2); // head
    px(g, 12, 3, 0xfff8d0);
  });
  tex(scene, TX.moon, 16, 16, (g) => {
    g.fillStyle(0xf6f2d8, 1); g.fillCircle(8, 8, 7);
    g.fillStyle(0xe4ddbd, 1); g.fillCircle(5, 6, 1.5); g.fillCircle(10, 10, 2); g.fillCircle(11, 5, 1);
  });
  tex(scene, TX.bunting, 16, 14, (g) => {
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, 16, 1); // string
    g.fillTriangle(2, 1, 14, 1, 8, 12); // pennant (tinted at runtime)
  });
}

/** A little bluebird with a 2-frame wing flap (no bird sprite exists in the packs). */
function drawBird(g: G, ox: number, wingsUp: boolean): void {
  const body = 0x5a8fd0;
  const dark = darken(body, 0.28);
  g.fillStyle(dark, 1); g.fillTriangle(ox + 1, 9, ox + 5, 8, ox + 4, 12); // tail
  g.fillStyle(body, 1); g.fillEllipse(ox + 8, 9, 9, 5); // body
  g.fillCircle(ox + 12, 7, 2.5); // head
  g.fillStyle(0xf2ecdc, 1); g.fillEllipse(ox + 8, 10, 5, 2.5); // pale belly
  px(g, ox + 14, 7, 0xe0a13a); // beak
  px(g, ox + 12, 6, PAL.ink); // eye
  g.fillStyle(dark, 1);
  if (wingsUp) g.fillTriangle(ox + 6, 8, ox + 9, 2, ox + 11, 8);
  else g.fillTriangle(ox + 6, 9, ox + 9, 14, ox + 11, 9);
}

function buildBird(scene: Phaser.Scene): void {
  if (scene.textures.exists(TX.bird)) return;
  const g = scene.add.graphics();
  g.setVisible(false);
  drawBird(g, 0, false);
  drawBird(g, 16, true);
  g.generateTexture(TX.bird, 32, 16);
  g.destroy();
  const t = scene.textures.get(TX.bird);
  t.add(0, 0, 0, 0, 16, 16);
  t.add(1, 0, 16, 0, 16, 16);
}

function buildAnimals(scene: Phaser.Scene): void {
  tex(scene, TX.chicken, TS, TS, (g) => {
    g.fillStyle(0xffffff, 1); g.fillCircle(8, 9, 4); g.fillCircle(8, 6, 2.5);
    px(g, 8, 3, PAL.red); px(g, 9, 3, PAL.red); // comb
    px(g, 11, 6, 0xe0a13a); // beak
    box(g, 6, 12, 1, 2, 0xe0a13a); box(g, 9, 12, 1, 2, 0xe0a13a); // legs
    px(g, 7, 6, PAL.ink); // eye
  });
  tex(scene, TX.cow, TS, TS, (g) => {
    g.fillStyle(0xf2efe6, 1); g.fillEllipse(7, 9, 11, 6); // body
    g.fillStyle(0x4a4036, 1); g.fillCircle(5, 8, 2); g.fillCircle(10, 10, 1.5); // spots
    box(g, 4, 12, 1, 3, 0x4a4036); box(g, 10, 12, 1, 3, 0x4a4036); // legs
    g.fillStyle(0xf2efe6, 1); g.fillCircle(13, 8, 2.5); // head
    px(g, 14, 8, PAL.ink); px(g, 13, 10, 0xe0a0a0); // eye + muzzle
  });
  tex(scene, TX.barn, 32, 32, (g) => {
    box(g, 3, 12, 26, 18, 0xb83a3a); // red barn body
    box(g, 3, 12, 26, 2, 0x8a2a2a);
    g.fillStyle(0x8a5a3a, 1); g.fillTriangle(1, 13, 16, 2, 31, 13); // roof
    box(g, 1, 11, 30, 3, 0x6e4a2e);
    box(g, 12, 18, 8, 12, 0x7a3030); // big door
    box(g, 15, 18, 2, 12, 0xe0c060); box(g, 12, 23, 8, 1, 0xe0c060); // door trim
    box(g, 5, 16, 5, 4, 0xf2e0b0); box(g, 22, 16, 5, 4, 0xf2e0b0); // windows
    px(g, 16, 6, 0xf2e0b0); // hay loft
  });
  tex(scene, TX.sheep, TS, TS, (g) => {
    g.fillStyle(0xf6f3ec, 1); g.fillEllipse(8, 9, 12, 8); // fluffy body
    g.fillCircle(4, 8, 3); g.fillCircle(7, 6, 3); g.fillCircle(10, 7, 3); g.fillCircle(12, 9, 2.5); g.fillCircle(6, 10, 3); // wool bumps
    g.fillStyle(0xe6e0d4, 1); px(g, 7, 10, 0xe6e0d4); px(g, 10, 10, 0xe6e0d4);
    g.fillStyle(0x423931, 1); g.fillCircle(12, 11, 2.6); // head
    px(g, 11, 8, 0x423931); px(g, 13, 8, 0x423931); // ears
    px(g, 13, 11, PAL.white); px(g, 12, 12, 0xd98a9a); // eye + nose
    box(g, 5, 14, 1, 2, 0x423931); box(g, 9, 14, 1, 2, 0x423931); // legs
  });
  tex(scene, TX.pig, TS, TS, (g) => {
    g.fillStyle(0xe8a0a8, 1); g.fillEllipse(7, 9, 12, 8); // pink body
    g.fillStyle(0xeeaab0, 1); g.fillCircle(12, 9, 3); // head
    box(g, 13, 8, 2, 3, 0xd98088); // snout
    px(g, 13, 9, 0xb86b73); px(g, 14, 9, 0xb86b73); // nostrils
    px(g, 11, 6, 0xd98088); px(g, 11, 7, PAL.white); // ear + eye
    box(g, 5, 14, 1, 2, 0xd98088); box(g, 9, 14, 1, 2, 0xd98088); // legs
    px(g, 2, 8, 0xeeaab0); px(g, 1, 9, 0xeeaab0); // curly tail
  });
  tex(scene, TX.duck, TS, TS, (g) => {
    g.fillStyle(0xf6f0e0, 1); g.fillEllipse(7, 10, 11, 7); g.fillCircle(11, 7, 2.6); // body + head
    box(g, 13, 7, 3, 2, 0xe8a838); // bill
    box(g, 5, 14, 2, 1, 0xe8a838); box(g, 9, 14, 2, 1, 0xe8a838); // feet
    px(g, 11, 6, 0x2a2a2a); // eye
    g.fillStyle(0xe6dcc6, 1); g.fillCircle(4, 10, 2.5); // wing
  });
  tex(scene, TX.egg, TS, TS, (g) => {
    g.fillStyle(0xf5ecd8, 1); g.fillEllipse(8, 9, 9, 12);
    g.fillStyle(0xffffff, 0.6); g.fillCircle(6, 7, 1.5);
  });
}

function buildInterior(scene: Phaser.Scene): void {
  tex(scene, TX.intFloor, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, PAL.wood1);
    box(g, 0, 5, TS, 1, PAL.wood2); box(g, 0, 11, TS, 1, PAL.wood2);
    px(g, 8, 2, lighten(PAL.wood1, 0.1)); px(g, 3, 8, lighten(PAL.wood1, 0.08));
  });
  tex(scene, TX.intWall, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, PAL.wall);
    box(g, 0, 9, TS, 1, darken(PAL.wall, 0.18));
    box(g, 0, 10, TS, 6, PAL.wood2); // baseboard
    px(g, 4, 3, lighten(PAL.wall, 0.1)); px(g, 11, 5, darken(PAL.wall, 0.1));
  });
  tex(scene, TX.intDoor, TS, TS, (g) => {
    box(g, 0, 0, TS, TS, PAL.wood2);
    box(g, 3, 1, 10, 15, PAL.door);
    px(g, 11, 9, PAL.gold); // knob
  });
  tex(scene, TX.chest, TS, TS, (g) => {
    box(g, 2, 6, 12, 8, PAL.wood1);
    box(g, 2, 4, 12, 3, PAL.wood2); // lid
    box(g, 2, 9, 12, 1, PAL.wood2);
    box(g, 7, 8, 2, 3, PAL.gold); // latch
    px(g, 7, 9, PAL.goldDark);
  });
  tex(scene, TX.workbench, TS, TS, (g) => {
    box(g, 1, 7, 14, 6, PAL.wood1); // bench top
    box(g, 1, 7, 14, 2, PAL.wood2);
    box(g, 2, 12, 2, 3, PAL.wood2); box(g, 12, 12, 2, 3, PAL.wood2); // legs
    box(g, 4, 5, 1, 2, 0x9aa0a8); px(g, 4, 4, PAL.wood2); // tool
    box(g, 9, 5, 3, 1, PAL.wood2); px(g, 11, 6, 0x9aa0a8); // saw
  });
  tex(scene, TX.stove, TS, TS, (g) => {
    box(g, 2, 5, 12, 10, 0x6a6e74); // body
    box(g, 2, 5, 12, 2, 0x8a8e94); // cooktop
    box(g, 4, 9, 8, 5, 0x2a2a2e); // oven door
    box(g, 5, 11, 6, 1, 0xffaa44); // oven glow
    px(g, 4, 6, PAL.red); px(g, 7, 6, PAL.red); px(g, 10, 6, 0xffaa44); // burners
  });
}

function buildHats(scene: Phaser.Scene): void {
  tex(scene, TX.hatStraw, 16, 16, (g) => {
    g.fillStyle(0xd9b066, 1); g.fillEllipse(8, 11, 14, 4); // brim
    box(g, 5, 5, 6, 6, 0xc99a4a); // crown
    box(g, 4, 10, 8, 1, darken(0xd9b066, 0.25)); // band
  });
  tex(scene, TX.hatBow, 16, 16, (g) => {
    g.fillStyle(0xe06d8b, 1); g.fillTriangle(8, 9, 2, 6, 2, 12); g.fillTriangle(8, 9, 14, 6, 14, 12); // wings
    box(g, 7, 7, 2, 5, darken(0xe06d8b, 0.2)); // knot
    px(g, 4, 8, lighten(0xe06d8b, 0.3)); px(g, 11, 8, lighten(0xe06d8b, 0.3));
  });
  tex(scene, TX.hatParty, 16, 16, (g) => {
    g.fillStyle(0x5ab0d8, 1); g.fillTriangle(8, 2, 4, 13, 12, 13); // cone
    g.fillStyle(PAL.gold, 1); g.fillCircle(8, 2, 1.5); // pom
    g.fillStyle(PAL.white, 1); px(g, 7, 7, PAL.white); px(g, 9, 10, PAL.white); px(g, 6, 11, PAL.white);
  });
  tex(scene, TX.hatCrown, 16, 16, (g) => {
    g.fillStyle(PAL.gold, 1); box(g, 4, 8, 8, 4, PAL.gold); // band
    g.fillTriangle(4, 8, 5, 4, 7, 8); g.fillTriangle(6, 8, 8, 3, 10, 8); g.fillTriangle(9, 8, 11, 4, 12, 8); // points
    px(g, 8, 10, PAL.red); px(g, 5, 10, 0x6fb0c8); px(g, 11, 10, 0x6fb0c8); // jewels
  });
}

function buildWorldTier2(scene: Phaser.Scene): void {
  tex(scene, TX.weed, TS, TS, (g) => {
    box(g, 7, 9, 2, 6, PAL.leaf2);
    g.fillStyle(PAL.leaf1, 1);
    g.fillTriangle(8, 6, 4, 12, 8, 12); g.fillTriangle(8, 6, 12, 12, 8, 12);
    g.fillTriangle(8, 4, 6, 9, 10, 9);
    px(g, 8, 5, lighten(PAL.leaf1, 0.3)); px(g, 5, 11, PAL.gold);
  });
  tex(scene, TX.shop, TS * 2, TS * 2, (g) => {
    box(g, 2, 18, 28, 12, PAL.wood1); box(g, 2, 18, 28, 2, PAL.wood2);
    box(g, 4, 28, 3, 4, PAL.wood2); box(g, 25, 28, 3, 4, PAL.wood2);
    box(g, 3, 4, 2, 16, PAL.wood2); box(g, 27, 4, 2, 16, PAL.wood2);
    for (let i = 0; i < 4; i++) box(g, 2 + i * 7, 2, 7, 6, i % 2 ? PAL.red : PAL.white);
    box(g, 2, 7, 28, 1, darken(PAL.red, 0.2));
    g.fillStyle(PAL.gold, 1); g.fillCircle(9, 16, 2);
    g.fillStyle(0xe05a4a, 1); g.fillCircle(15, 16, 2);
    g.fillStyle(0xc9a0dc, 1); g.fillCircle(21, 16, 2);
  });
}

// ---- public entry -------------------------------------------------------------

export function generatePlaceholderTextures(scene: Phaser.Scene): void {
  buildTileset(scene);
  buildCharacters(scene);
  buildStructures(scene);
  buildDecorations(scene);
  buildCrops(scene);
  buildItemIcons(scene);
  buildTools(scene);
  buildWorldTier2(scene);
  buildAmbient(scene);
  buildBird(scene);
  buildAnimals(scene);
  buildInterior(scene);
  buildHats(scene);
  buildForest(scene);
  buildBeach(scene);
  buildMine(scene);
  buildUi(scene);
}
